import React from 'react';
import styled from 'styled-components';
import { Github, Info, Columns3 } from 'lucide-react';

const HeaderContainer = styled.header`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.95) 0%, rgba(118, 75, 162, 0.95) 100%);
  backdrop-filter: blur(10px);
  color: white;
  padding: 14px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  min-height: 52px;
  
  @media (max-width: 768px) {
    padding: 10px 12px;
    min-height: 48px;
  }
  
  @media (max-width: 480px) {
    padding: 8px 8px;
    min-height: 44px;
    flex-direction: column;
    gap: 8px;
  }
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 36px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    gap: 8px;
    font-size: 28px;
  }
  
  @media (max-width: 480px) {
    gap: 6px;
    font-size: 24px;
  }
`;

const Title = styled.h1`
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 20px;
  }
  
  @media (max-width: 480px) {
    font-size: 16px;
  }
`;

const Navigation = styled.nav`
  display: flex;
  align-items: center;
  gap: 16px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
  
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 4px;
  }
`;

const NavLink = styled.a`
  color: white;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 6px;
  transition: background-color 0.2s;
  font-size: 18px;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
  }
  
  @media (max-width: 768px) {
    font-size: 14px;
    padding: 4px 8px;
    gap: 4px;
  }
  
  @media (max-width: 480px) {
    font-size: 12px;
    padding: 3px 6px;
    gap: 3px;
    
    span:last-child {
      display: none;
    }
  }
`;

const InfoButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 6px 12px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 14px;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    border-color: rgba(255, 255, 255, 0.3);
  }
  
  @media (max-width: 768px) {
    font-size: 12px;
    padding: 4px 8px;
    gap: 4px;
  }
  
  @media (max-width: 480px) {
    font-size: 11px;
    padding: 3px 6px;
    gap: 3px;
    
    span:last-child {
      display: none;
    }
  }
`;

interface AppHeaderProps {
  onInfoClick?: () => void;
  onCompareClick?: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({ onInfoClick, onCompareClick }) => {
  return (
    <HeaderContainer>
      <Logo>
        <span>🌍</span>
        <div>
          <Title>ISO 3166 Flags Explorer</Title>
        </div>
      </Logo>

      <Navigation>
        <NavLink 
          href="https://github.com/amckenna41/iso3166-flags" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <Github size={20} />
          Flags Dataset
        </NavLink>
        <NavLink 
          href="https://github.com/amckenna41/iso3166-2" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <Github size={20} />
          Subdivision Data
        </NavLink>
        <NavLink 
          href="https://iso3166-2-api.vercel.app/api" 
          target="_blank" 
          rel="noopener noreferrer"
        >
          <Github size={20} />
          Subdivision API
        </NavLink>
        {onCompareClick && (
          <InfoButton type="button" onClick={onCompareClick} title="Compare countries side by side">
            <Columns3 size={16} />
            Compare
          </InfoButton>
        )}
        {onInfoClick && (
          <InfoButton type="button" onClick={onInfoClick}>
            <Info size={16} />
            About
          </InfoButton>
        )}
      </Navigation>
    </HeaderContainer>
  );
};

export default AppHeader;